import { Box, Button, Card, Modal, Typography } from '@mui/material';
import { Option, SelectedOption, UserQuestion } from '../__generated__/graphql';
import OptionCard from './OptionCard';
import { useState } from 'react';
import { QuestionCardStyles } from '../styles/QuestionCardStyles';
import { getOptionStatusFromSelectedOption } from '../util/Option';
import { getCharCode } from '../util/Common';
import CloseIcon from '@mui/icons-material/Close';
import { tagToColor } from '../types/Tag';

interface QuestionCardProps {
  userQuestion: UserQuestion;
  index: number;
  onSelectedOption: (questionId: string, option: SelectedOption) => void;
  disabled?: boolean;
  onClearAnswers?: () => void;
  showTag?: boolean;
}

const QuestionCard = ({
  userQuestion,
  index,
  onSelectedOption,
  disabled = false,
  onClearAnswers,
  showTag = false,
}: QuestionCardProps) => {
  const [isExplainModalOpen, setExplainModalOpen] = useState<boolean>(false);

  const question = userQuestion.question;

  const handleSelectedOption = (option: Option) => {
    if (disabled) return;

    const newSelectedOption: SelectedOption = {
      desc: option.desc,
      isAnswer: option.isAnswer,
    };
    onSelectedOption(userQuestion.question.id, newSelectedOption);
  };

  return (
    <Box sx={{ img: { maxWidth: '500px', maxHeight: '400px' } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          marginTop: '20px',
        }}
      >
        <Typography sx={QuestionCardStyles.header}>
          {`Question ${index + 1}`}
          {question.label && ` - ${question.label}`}
        </Typography>
        {showTag && (
          <Typography
            sx={{
              backgroundColor: tagToColor[question.tag] || '#BDBDBD',
              color: '#FFF',
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'capitalize',
              marginBottom: '22px',
            }}
          >
            {question.tag}
          </Typography>
        )}
      </Box>
      <Box
        sx={QuestionCardStyles.prompt}
        dangerouslySetInnerHTML={{ __html: question.prompt }}
      />
      <Box sx={QuestionCardStyles.optionsContainer}>
        {question.options.map((option, optionIndex) => (
          <OptionCard
            key={`${question.id}-${optionIndex}`}
            letter={String.fromCharCode(optionIndex + getCharCode('A'))}
            option={option}
            status={getOptionStatusFromSelectedOption(
              userQuestion.selectedOption,
              option,
            )}
            disabled={!!userQuestion.selectedOption.desc || disabled}
            onSelectOption={(option) => handleSelectedOption(option)}
          />
        ))}
      </Box>
      <Box
        sx={{
          ...QuestionCardStyles.buttonContainer,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {userQuestion.selectedOption?.desc && (
          <Button
            sx={QuestionCardStyles.explButton}
            color="warning"
            onClick={() => setExplainModalOpen(true)}
          >
            See Explanation
          </Button>
        )}
        {onClearAnswers && (
          <Button
            sx={{ ...QuestionCardStyles.explButton, color: 'gray' }}
            onClick={onClearAnswers}
          >
            Reset Options
          </Button>
        )}
      </Box>
      <Modal
        open={isExplainModalOpen}
        onClose={() => setExplainModalOpen(false)}
      >
        <Box sx={QuestionCardStyles.modalContainer}>
          <Card sx={QuestionCardStyles.modalContent}>
            <Box sx={QuestionCardStyles.closeIconContainer}>
              <Button
                variant="outlined"
                sx={QuestionCardStyles.closeIconButton}
                onClick={() => setExplainModalOpen(false)}
              >
                <CloseIcon sx={QuestionCardStyles.closeIcon} />
              </Button>
            </Box>
            <Typography variant="h6" sx={QuestionCardStyles.modalHeader}>
              Explanation
            </Typography>
            <Box
              sx={QuestionCardStyles.prompt}
              dangerouslySetInnerHTML={{ __html: question.explanation ?? '' }}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setExplainModalOpen(false)}
              sx={QuestionCardStyles.modalCloseButton}
            >
              Close
            </Button>
          </Card>
        </Box>
      </Modal>
    </Box>
  );
};

export default QuestionCard;
